import { useState } from 'react';
import { motion } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import type { SavedLesson, PracticeTest } from '@/mocks/lessons';

interface PracticeTestsProps {
  lesson: SavedLesson;
}

const difficultyBadge = (difficulty: PracticeTest['difficulty'], isDark: boolean) => {
  switch (difficulty) {
    case 'easy':
      return { label: 'Easy', color: isDark ? 'text-emerald-400' : 'text-emerald-600', bg: isDark ? 'bg-emerald-500/8' : 'bg-emerald-50', border: isDark ? 'border-emerald-500/12' : 'border-emerald-200/50' };
    case 'medium':
      return { label: 'Medium', color: isDark ? 'text-amber-400' : 'text-amber-600', bg: isDark ? 'bg-amber-500/8' : 'bg-amber-50', border: isDark ? 'border-amber-500/12' : 'border-amber-200/50' };
    case 'hard':
      return { label: 'Hard', color: isDark ? 'text-rose-400' : 'text-rose-600', bg: isDark ? 'bg-rose-500/8' : 'bg-rose-50', border: isDark ? 'border-rose-500/12' : 'border-rose-200/50' };
  }
};

export default function PracticeTests({ lesson }: PracticeTestsProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [startedTest, setStartedTest] = useState<string | null>(null);

  const t = {
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textMuted: isDark ? 'text-white/30' : 'text-black/30',
    textFaint: isDark ? 'text-white/25' : 'text-black/25',
    iconBg: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]',
    iconMuted: isDark ? 'text-white/50' : 'text-black/40',
    cardBg: isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]',
    cardBorder: isDark ? 'border-white/[0.05]' : 'border-black/[0.05]',
    cardBorderHover: isDark ? 'hover:border-white/[0.10]' : 'hover:border-black/[0.10]',
    playIconBg: isDark ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-black/[0.04] border-black/[0.06]',
    playIcon: isDark ? 'text-white/50' : 'text-black/40',
    cardDivider: isDark ? 'border-white/[0.04]' : 'border-black/[0.04]',
    btnPrimary: isDark ? 'bg-white text-black' : 'bg-black text-white',
    subText: isDark ? 'text-white/30' : 'text-black/30',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
      className="px-5 pb-4"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-8 h-8 rounded-lg ${t.iconBg} flex items-center justify-center`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-questionnaire-line ${t.iconMuted} text-sm`} />
          </div>
        </div>
        <div>
          <h3 className={`${t.textPrimary} text-sm font-semibold`}>Practice Tests</h3>
          <p className={`${t.textFaint} text-[11px]`}>{lesson.practiceTests.length} tests available</p>
        </div>
      </div>

      <div className="space-y-2.5">
        {lesson.practiceTests.map((test, i) => {
          const badge = difficultyBadge(test.difficulty, isDark);
          const isStarted = startedTest === test.id;

          return (
            <motion.div
              key={test.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.3 }}
            >
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => setStartedTest(isStarted ? null : test.id)}
                className={`w-full text-left p-4 rounded-2xl ${t.cardBg} border ${t.cardBorder} ${t.cardBorderHover} transition-all duration-200 cursor-pointer`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${badge.color} ${badge.bg} ${badge.border} border`}>
                        {badge.label}
                      </span>
                      <span className={`${t.textFaint} text-[10px]`}>{test.duration} min</span>
                    </div>
                    <h4 className={`${t.textPrimary} text-sm font-semibold mb-1`}>{test.title}</h4>
                    <p className={`${t.textMuted} text-[11px]`}>{test.questionCount} questions</p>
                  </div>
                  <div className={`w-10 h-10 rounded-xl ${t.playIconBg} flex items-center justify-center flex-shrink-0`}>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${isStarted ? 'ri-pause-fill' : 'ri-play-fill'} ${t.playIcon} text-sm`} />
                    </div>
                  </div>
                </div>

                {isStarted && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className={`mt-3 pt-3 border-t ${t.cardDivider}`}
                  >
                    <p className={`${t.subText} text-[11px] mb-3`}>Test session started. Answer all questions to see your score.</p>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      className={`w-full py-2.5 rounded-xl ${t.btnPrimary} text-xs font-semibold cursor-pointer`}
                    >
                      Continue Test
                    </motion.button>
                  </motion.div>
                )}
              </motion.button>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}